import { useMemo } from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { Check } from "lucide-react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import {
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { KpiTile } from "@/components/cinch/KpiTile";
import { ProductStatePill } from "@/components/cinch/ProductStatePill";
import { TierChip } from "@/components/cinch/TierChip";
import { useCan } from "@/lib/auth";
import { cn } from "@/lib/utils";

import type { UpsellOpsSearch } from "..";
import {
  useUpsellCsmOptions,
  useWhitespace,
  type WhitespaceCustomer,
  type WhitespaceGroup,
} from "../api";
import {
  BAR_CLASS,
  filterCustomers,
  groupStats,
  openCount,
  pillColor,
  sortCustomers,
  totalOpenUpsells,
  type SortKey,
  type WhitespaceFilters,
} from "../lib";
import { GroupConfigTab } from "../components/GroupConfigTab";
import { UpsellAccountDrawer } from "../components/UpsellAccountDrawer";
import { fmtCur } from "../../renewals/format";

const SORT_LABELS: { key: SortKey; label: string }[] = [
  { key: "op", label: "Most opportunity" },
  { key: "name", label: "A–Z" },
  { key: "newest", label: "Newest" },
  { key: "tier", label: "Tier" },
  { key: "arr", label: "ARR" },
];

export function UpsellOpsPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/upsell-ops" }) as UpsellOpsSearch;
  const canAdmin = useCan("upsell_ops.admin");
  const tab = search.tab === "config" && canAdmin ? "config" : "dashboard";

  const setSearch = (next: Partial<UpsellOpsSearch>) =>
    navigate({
      to: "/upsell-ops",
      search: (prev: UpsellOpsSearch) => ({ ...prev, ...next }),
    });

  const data = useWhitespace();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Upsell Ops</h1>
          <p className="text-sm text-muted-foreground">
            Product whitespace across active customers — who's billing ($), who's
            on contract (✓), and where the open upsells are.
          </p>
        </div>
        {data.data && (
          <div className="text-right text-xs text-muted-foreground">
            {/* Two independent freshness stamps: the billing month drives the
                $ layer; the contract cache drives the ✓ layer and is only
                refreshable by billing-capable users — everyone else needs to
                SEE how old it is. */}
            Billing month ($): <b className="text-foreground">{data.data.month}</b>
            {" · "}Source: BI.REVENUE_WATERFALL
            <br />
            Contracts (✓) synced:{" "}
            {data.data.contract_cache.last_synced_at ? (
              <b
                className="text-foreground"
                title={
                  data.data.contract_cache.status &&
                  data.data.contract_cache.status !== "ok"
                    ? `${data.data.contract_cache.status}: ${data.data.contract_cache.detail ?? ""}`
                    : undefined
                }
              >
                {new Date(data.data.contract_cache.last_synced_at).toLocaleString()}
                {data.data.contract_cache.status &&
                  data.data.contract_cache.status !== "ok" &&
                  ` (${data.data.contract_cache.status})`}
              </b>
            ) : (
              <b className="text-warning">never</b>
            )}
          </div>
        )}
      </div>

      {canAdmin && (
        <div className="flex flex-wrap gap-1 border-b border-border">
          {(["dashboard", "config"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setSearch({ tab: t === "dashboard" ? undefined : t })}
              className={cn(
                "rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium capitalize",
                tab === t
                  ? "border-brand text-brand"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t === "config" ? "Config" : "Dashboard"}
            </button>
          ))}
        </div>
      )}

      {tab === "config" ? (
        <GroupConfigTab contractCache={data.data?.contract_cache ?? null} />
      ) : (
        <DashboardTab
          search={search}
          setSearch={setSearch}
          loading={data.isLoading}
          error={data.error ? String(data.error) : null}
          groups={data.data?.groups ?? []}
          customers={data.data?.customers ?? []}
        />
      )}
    </div>
  );
}

function DashboardTab({
  search,
  setSearch,
  loading,
  error,
  groups,
  customers,
}: {
  search: UpsellOpsSearch;
  setSearch: (next: Partial<UpsellOpsSearch>) => void;
  loading: boolean;
  error: string | null;
  groups: WhitespaceGroup[];
  customers: WhitespaceCustomer[];
}) {
  const csmOptions = useUpsellCsmOptions();

  const filters: WhitespaceFilters = useMemo(
    () => ({
      q: search.q ?? "",
      tiers: (search.tiers ?? "")
        .split(",")
        .map(Number)
        .filter((n) => Number.isInteger(n) && n >= 1 && n <= 5),
      csm: search.csm ?? null,
      missing: search.missing ?? null,
      legacy: !!search.legacy,
      openOnly: !!search.open,
    }),
    [search],
  );
  const sort: SortKey = search.sort ?? "op";

  // KPI cards reflect the working cohort (legacy/tier/CSM filters) but ignore
  // the search box and any card's own missing-product filter, so clicking a
  // card never changes the cards themselves.
  const cardPool = useMemo(
    () => filterCustomers(customers, groups, { ...filters, q: "", missing: null, openOnly: false }),
    [customers, groups, filters],
  );
  const rows = useMemo(
    () => sortCustomers(filterCustomers(customers, groups, filters), groups, sort),
    [customers, groups, filters, sort],
  );

  const csmComboOptions = useMemo<ComboOption[]>(
    () =>
      (csmOptions.data?.options ?? []).map((o) => ({
        id: o.value,
        label: o.label,
        group: o.kind === "partner" ? "Managing partners" : "CSMs",
        badge: String(o.account_count),
      })),
    [csmOptions.data],
  );
  const selectedCsmLabel = csmComboOptions.find((o) => o.id === filters.csm)?.label;

  const groupKeys = groups.map((g) => g.key);
  const columns: DataTableColumn<WhitespaceCustomer>[] = [
    {
      key: "name",
      header: "Customer",
      className: "max-w-[340px]",
      render: (c) => (
        <div>
          <button
            type="button"
            className="text-left text-sm font-semibold hover:text-brand hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setSearch({ account: c.qb_id, expanded: true });
            }}
            title="Open the full account view"
          >
            {c.name ?? `QB-${c.qb_id}`}
            {!c.platform && (
              <span className="ml-2 rounded border border-warning/40 bg-warning/10 px-1 py-px align-middle text-[9px] font-bold uppercase tracking-wider text-warning">
                legacy
              </span>
            )}
          </button>
          <div className="text-xs text-muted-foreground">
            Customer since {c.since_year ?? "—"}
          </div>
        </div>
      ),
    },
    {
      key: "tier",
      header: "Tier",
      align: "center",
      render: (c) => <TierChip tier={c.tier} />,
    },
    {
      key: "csm",
      header: "CSM",
      render: (c) => (
        <span className={cn("text-sm", !c.csm_label && "text-muted-foreground")}>
          {c.csm_label ?? "—"}
        </span>
      ),
    },
    ...groups.map(
      (g): DataTableColumn<WhitespaceCustomer> => ({
        key: `product_${g.key}`,
        header: g.label,
        align: "center",
        render: (c) => (
          <ProductStatePill state={c.products[g.key] ?? "none"} color={pillColor(g.color)} />
        ),
      }),
    ),
    {
      key: "open",
      header: "Open",
      align: "right",
      render: (c) => {
        const n = openCount(c, groupKeys);
        return (
          <span
            className={cn(
              "inline-flex h-6 min-w-[1.6rem] items-center justify-center rounded-full px-2 text-xs font-bold",
              n === 0
                ? "bg-success-light text-success"
                : n <= 2
                  ? "bg-brand-light text-brand"
                  : "bg-orange-light text-orange",
            )}
          >
            {n}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}
      >
        {groups.map((g) => {
          const stats = groupStats(cardPool, g.key);
          const color = pillColor(g.color);
          const active = filters.missing === g.key;
          return (
            <KpiTile
              key={g.key}
              label={g.label}
              value={
                <>
                  {stats.attachPct}%
                  <span className="ml-1.5 text-sm font-semibold text-muted-foreground">
                    attached
                  </span>
                </>
              }
              sub={
                <>
                  {stats.attached}/{stats.total} customers ({stats.billed} billing) ·{" "}
                  <b className="text-orange">{stats.whitespace}</b> whitespace
                </>
              }
              swatchClass={BAR_CLASS[color]}
              progress={{ pct: stats.attachPct, barClass: BAR_CLASS[color] }}
              onClick={() => setSearch({ missing: active ? undefined : g.key })}
              selected={active}
            />
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border border-border bg-card px-4 py-2.5 text-sm">
        <span>
          <b className="text-base">{cardPool.length}</b> customers
        </span>
        <span>
          Total open upsells:{" "}
          <b className="text-base text-orange">{totalOpenUpsells(cardPool, groups)}</b>
        </span>
        {filters.missing && (
          <span className="text-xs text-muted-foreground">
            Showing customers missing{" "}
            <b className="text-foreground">
              {groups.find((g) => g.key === filters.missing)?.label}
            </b>{" "}
            —{" "}
            <button
              type="button"
              className="font-semibold text-brand hover:underline"
              onClick={() => setSearch({ missing: undefined })}
            >
              clear
            </button>
          </span>
        )}
      </div>

      <FilterBar>
        <FilterSearch
          placeholder="Search customer name…"
          value={filters.q}
          onChange={(v) => setSearch({ q: v || undefined })}
        />
        <FilterChip label="Tier" count={filters.tiers.length}>
          <CheckboxList
            options={[1, 2, 3, 4, 5].map((t) => ({ value: String(t), label: `T${t}` }))}
            selected={filters.tiers.map(String)}
            onChange={(v) => setSearch({ tiers: v.length ? v.join(",") : undefined })}
          />
        </FilterChip>
        <FilterChip label="CSM" valueLabel={selectedCsmLabel}>
          <Combobox
            options={csmComboOptions}
            value={filters.csm}
            onChange={(v) => setSearch({ csm: v ?? undefined })}
            placeholder="All CSMs"
            searchPlaceholder="Search CSMs…"
            emptyText={csmOptions.isLoading ? "Loading…" : "No CSMs found."}
          />
        </FilterChip>
        <FilterChip
          label="More"
          count={(filters.legacy ? 1 : 0) + (filters.openOnly ? 1 : 0)}
        >
          <CheckboxList
            options={[
              { value: "open", label: "Has open upsells only" },
              { value: "legacy", label: "Show legacy / non-platform" },
            ]}
            selected={[
              ...(filters.openOnly ? ["open"] : []),
              ...(filters.legacy ? ["legacy"] : []),
            ]}
            onChange={(v) =>
              setSearch({
                open: v.includes("open") || undefined,
                legacy: v.includes("legacy") || undefined,
              })
            }
          />
        </FilterChip>
        <div className="flex-1" />
        {/* Sort is a single-select chip so the whole bar stays one DS FilterBar
            row (matches the CSM chip's popover idiom). Only shows a value label
            when it's off the default. */}
        <FilterChip
          label="Sort"
          valueLabel={
            sort !== "op" ? SORT_LABELS.find((s) => s.key === sort)?.label : undefined
          }
        >
          <div className="flex flex-col gap-0.5">
            {SORT_LABELS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSearch({ sort: s.key === "op" ? undefined : s.key })}
                className={cn(
                  "flex items-center justify-between rounded-md px-2 py-1.5 text-left text-sm",
                  sort === s.key
                    ? "bg-brand-light font-medium text-brand"
                    : "hover:bg-muted",
                )}
              >
                {s.label}
                {sort === s.key && <Check className="h-3.5 w-3.5" />}
              </button>
            ))}
          </div>
        </FilterChip>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(c) => String(c.qb_id)}
        loading={loading}
        error={error ?? undefined}
        emptyMessage="No customers match."
        onRowClick={(c) => setSearch({ account: c.qb_id })}
        selectedKey={search.account != null ? String(search.account) : undefined}
      />

      <p className="text-xs text-muted-foreground">
        {rows.length} customer{rows.length === 1 ? "" : "s"} shown
        {filters.legacy ? " (incl. legacy)" : ""} ·{" "}
        {fmtCur(rows.reduce((s, c) => s + c.arr, 0))} combined ARR
      </p>

      <div className="flex flex-wrap gap-5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <ProductStatePill state="billed" color="brand" /> Billing — on this month's invoice
        </span>
        <span className="inline-flex items-center gap-1.5">
          <ProductStatePill state="contracted" color="brand" /> On an active contract, not billing
        </span>
        <span className="inline-flex items-center gap-1.5">
          <ProductStatePill state="none" /> Whitespace — open upsell
        </span>
      </div>

      <UpsellAccountDrawer customers={customers} groups={groups} />
    </>
  );
}
